/**
 * Build a URL back to the supplier store that preserves the full
 * shared-quote / simulation context.
 *
 * Used by:
 *  - "Continuar comprando" button in CartSheet
 *  - "Cardápios" back button in SupplierMenuPage
 *
 * Without these params, the store re-mounts as a fresh visit and
 * the client loses the original quoted categories and substitutions.
 */
export const buildStoreReturnUrl = (supplierSlug: string): string => {
  const currentParams = new URLSearchParams(window.location.search);
  const params = new URLSearchParams();

  const isSharedSession = sessionStorage.getItem('ameup_is_shared_session') === 'true';
  const isQuoteMode = sessionStorage.getItem('ameup_quote_mode') === 'true'
    || currentParams.get('mode') === 'orcamento';

  // Quote mode flag (supplier-side editor)
  if (isQuoteMode) params.set('mode', 'orcamento');

  // Shared-quote identifiers (client-side view of supplier-built quote)
  // CRITICAL: Only pull from sessionStorage if we are in a shared session or quote mode.
  // In autonomous sessions, ref/pid exist in storage for tracking but must NOT 
  // be added to the URL, as it triggers restricted "shared view" in the storefront.
  const ref = currentParams.get('ref') || (isSharedSession ? sessionStorage.getItem('ameup_event_code') : null);
  const pid = currentParams.get('pid') || (isSharedSession || isQuoteMode ? sessionStorage.getItem('ameup_parent_event_id') : null);
  const simId = currentParams.get('simulationId') || (isSharedSession ? sessionStorage.getItem('ameup_simulation_id') : null);
  const nome = currentParams.get('nome') || (isSharedSession ? sessionStorage.getItem('shared_cart_client_name') : null);

  if (ref) params.set('ref', ref);
  if (pid) params.set('pid', pid);
  if (simId) params.set('simulationId', simId);
  if (nome) params.set('nome', nome);

  // Quoted product IDs — keeps the supplier's original categories visible
  const itemsFromUrl = currentParams.get('items');
  let itemsCsv = itemsFromUrl || '';
  if (!itemsCsv) {
    try {
      const persisted = sessionStorage.getItem('ameup_shared_quoted_ids');
      if (persisted) {
        const arr = JSON.parse(persisted);
        if (Array.isArray(arr) && arr.length > 0) itemsCsv = arr.join(',');
      }
    } catch {}
  }
  if (itemsCsv) params.set('items', itemsCsv);

  // Persist the current search query if it exists
  const search = sessionStorage.getItem('ameup_store_search');
  if (search) params.set('search', search);

  // Filters (CEP, date, guests) — the per-supplier session is the source of
  // truth: it is updated by every editor (loja, /cardapio, carrinho, infos-festa).
  // The current URL may still carry the ORIGINAL values from the shared link,
  // so it is only a fallback.
  let cep = '';
  let date = '';
  let guests = '';
  try {
    const sf = sessionStorage.getItem(`store_filters_${supplierSlug}`);
    if (sf) {
      const parsed = JSON.parse(sf);
      if (parsed?.cep) cep = String(parsed.cep);
      if (parsed?.date) date = String(parsed.date);
      if (parsed?.guests) guests = String(parsed.guests);
    }
  } catch {}
  if (!cep) cep = currentParams.get('cep') || '';
  if (!date) date = currentParams.get('date') || '';
  if (!guests) guests = currentParams.get('guests') || '';

  if (cep) params.set('cep', cep);
  if (date) params.set('date', date);
  if (guests) params.set('guests', guests);

  const qs = params.toString();
  return `/loja/${supplierSlug}${qs ? `?${qs}` : ''}`;
};
